import type { Layout } from "react-grid-layout";
import Link from "next/link";
import { v4 as uuidv4 } from "uuid";
import { Button, Dropdown, Modal, Select, Tooltip, theme } from "antd";
import {
  ComponentProps,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ApiOutlined,
  BarChartOutlined,
  CopyOutlined,
  EditOutlined,
  EllipsisOutlined,
  FilterOutlined,
  FullscreenOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  LoadingOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { createPortal } from "react-dom";
import { useDebounce, useHover } from "usehooks-ts";
import {
  DashboardV1State,
  DeepLinkType,
  SnippetV1State,
  ExploreCard,
  MarkdownCard,
} from "pages/api/deep-links/types";
import { Flex } from "src/components/Flex";
import {
  ensureState,
  serializeParams,
  unserializeParams,
} from "src/search/lib/params-state-reducer";
import { useOrg } from "src/hooks/useOrg";
import { useSearch } from "src/search/useSearchContext";
import { EntityPreview } from "src/search/components/EntityPreview";
import { SubQueryPreview } from "src/search/components/SubQueryPreview";
import { AsteriskIcon } from "src/components/AsteriskIcon";
import { Condition, SearchActions, SearchState } from "src/search/types";
import { useSnippets } from "src/hooks/useSnippets";
import { asKey, fromKey } from "src/utils/fields";
import { mono } from "src/fonts";
import { QuestionInput } from "src/search/components/inputs/QuestionInput";
import { CssVars } from "src/components/CssVars";
import { usePopupContext } from "src/hooks/usePopup";
import { SnippetMenu } from "src/search/components/inputs/question-input/SnippetMenu";
import { useUniqueId } from "src/hooks/useUniqueId";
import { useEntitySelectors } from "src/search/useEntitySelectors";
import { cloneData } from "src/utils/objects";
import { useApplicationContext } from "src/hooks/useApplication";
import { SparkleIcon } from "src/components/SparkleIcon";
import { ChatAssistant } from "src/search/components/ChatAssistant";
import { serializeTabs } from "../explore/lib/useTabs";
import { serializeDocument } from "../explore/lib/useExploreDocument";
import { ExploreContext, useExplore } from "../explore/useExploreContext";
import { ResponseCanvas } from "../explore/ResponseCanvas";
import { TextualQuery } from "../explore/TextualQuery";
import { serializeContentView } from "../explore/lib/useContentView";
import { QuestionEditor } from "./QuestionEditor";
import { VisualizationMenu } from "./VisualizationMenu";
import { DashboardRuntime } from "./context";
import { DebugButton } from "src/components/DebugButton";

export function QuestionViewer({
  cardId,
  colorTheme,
  isLocked,
  json,
  onRemoveCard,
  saveDashboard,
  searchState,
  setCardData,
  setData,
  setEditor,
  setLayout,
  source,
  unlock,
}: {
  cardId: string;
  colorTheme?: string;
  isLocked: boolean;
  json: Pick<DashboardV1State, "data">;
  onRemoveCard(id: string): void;
  saveDashboard: DashboardRuntime["onSave"];
  searchState: Partial<SearchState>;
  setCardData(cardData: DashboardV1State["data"][string]): void;
  setData: Dispatch<SetStateAction<Record<string, ExploreCard | MarkdownCard>>>;
  setEditor(editor: null | JSX.Element): void;
  setLayout: Dispatch<SetStateAction<DashboardV1State["layout"]>>;
  source: DashboardRuntime["source"];
  unlock(): void;
}) {
  const card = json.data[cardId];
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");
  return card ? (
    <LoadedQuestion
      {...{
        cardId,
        colorTheme,
        isLocked,
        json,
        onRemoveCard,
        saveDashboard,
        searchState,
        setCardData,
        setData,
        setEditor,
        setLayout,
        source,
        unlock,
      }}
    />
  ) : (
    <Flex centered style={{ height: "100%" }}>
      <LoadingOutlined />
    </Flex>
  );
}

const TITLEBAR_HEIGHT = 30;

function LoadedQuestion(props: {
  cardId: string;
  colorTheme?: string;
  isLocked: boolean;
  json: Pick<DashboardV1State, "data">;
  onRemoveCard(cardId: string): void;
  saveDashboard: DashboardRuntime["onSave"];
  searchState: Partial<SearchState>;
  setCardData(cardData: DashboardV1State["data"][string]): void;
  setData: Dispatch<SetStateAction<Record<string, ExploreCard | MarkdownCard>>>;
  setEditor(editor: null | JSX.Element): void;
  setLayout: Dispatch<SetStateAction<DashboardV1State["layout"]>>;
  source: DashboardRuntime["source"];
  unlock(): void;
}) {
  const card = props.json.data[props.cardId];
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");

  const {
    explore,
    changed,
    reset,
    baseParams,
    resetBaseParams,
    setBaseParams,
  } = useQuestionCard(props);
  const entityPreview = Boolean(explore.entity.uuid);
  const subqueryPreview = Boolean(explore.subquery.state);
  return (
    <>
      {props.isLocked ? (
        <LockedQuestion
          key="locked-question"
          cardId={props.cardId}
          colorTheme={props.colorTheme}
          context={explore}
          changed={changed}
          json={props.json}
          onReset={reset}
          setBaseParams={setBaseParams}
          resetBaseParams={resetBaseParams}
          baseParams={baseParams}
          searchState={props.searchState}
          saveDashboard={props.saveDashboard}
          setData={props.setData}
          setLayout={props.setLayout}
          source={props.source}
          unlock={props.unlock}
        />
      ) : (
        <UnlockedQuestion
          key="unlocked-question"
          context={explore}
          colorTheme={props.colorTheme}
          changed={changed}
          onReset={reset}
          json={props.json}
          cardId={props.cardId}
          onRemoveCard={props.onRemoveCard}
          setCardData={props.setCardData}
          setEditor={props.setEditor}
          setBaseParams={setBaseParams}
          source={props.source}
          resetBaseParams={resetBaseParams}
          baseParams={baseParams}
          searchState={props.searchState}
          saveDashboard={props.saveDashboard}
          setData={props.setData}
          setLayout={props.setLayout}
          unlock={props.unlock}
        />
      )}
      {entityPreview && (
        <EntityPreview key="entity-preview" context={explore} />
      )}
      {subqueryPreview && (
        <SubQueryPreview
          config={{ openInExplore: false }}
          key="subquery-preview"
          context={explore}
        />
      )}
    </>
  );
}

function LockedQuestion(props: {
  context: ExploreContext;
  colorTheme?: string;
  changed: boolean;
  onReset(): void;
  json: Pick<DashboardV1State, "data">;
  cardId: string;
  resetBaseParams: () => void;
  searchState: Partial<SearchState>;
  setBaseParams(state: Partial<SearchState>): void;
  baseParams: Partial<SearchState> | undefined;
  saveDashboard: DashboardRuntime["onSave"];
  setData: Dispatch<SetStateAction<Record<string, ExploreCard | MarkdownCard>>>;
  setLayout: Dispatch<SetStateAction<DashboardV1State["layout"]>>;
  source: DashboardRuntime["source"];
  unlock(): void;
}) {
  const card = props.json.data[props.cardId];
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");

  const { token } = theme.useToken();
  // console.log("render: LOCKED-QUESTION");
  return (
    <div
      style={{
        height: "100%",
        position: "relative",
        backgroundColor: card.transparent
          ? "transparent"
          : props.colorTheme === "alternate"
          ? token.colorBgContainer
          : token.colorBgLayout,
        borderRadius: 10,
      }}
    >
      <Titlebar
        {...props}
        card={props.json.data[props.cardId] as ExploreCard}
      />
      <div
        style={{
          height: `calc(100% - ${TITLEBAR_HEIGHT + 8}px)`,
          overflow: "visible",
          borderRadius: 14,
          padding: 4,
        }}
      >
        <ResponseCanvas context={props.context} />
      </div>
    </div>
  );
}

function ModalQueryEditor(props: {
  initial: SearchState;
  onOk(state: SearchState): void;
  onClose(): void;
}) {
  const [state, setState] = useState(props.initial);
  const id = useUniqueId();

  return (
    <Modal
      open
      closable
      maskClosable
      onOk={() => {
        props.onOk(state);
        props.onClose();
      }}
      onCancel={() => props.onClose()}
      zIndex={999}
    >
      <CssVars>
        <span style={mono.style}>Manually adjust card query state:</span>
        <div style={{ margin: "8px 0 16px 0" }}>
          <QuestionInput
            seed={id}
            value={state}
            onChange={setState}
            options={{}}
          />
        </div>
      </CssVars>
    </Modal>
  );
}

function Titlebar(props: {
  context: ExploreContext;
  changed: boolean;
  onReset(): void;
  searchState: Partial<SearchState>;
  setBaseParams(state: Partial<SearchState>): void;
  resetBaseParams: () => void;
  baseParams: Partial<SearchState> | undefined;
  card: ExploreCard;
  cardId: string;
  saveDashboard: DashboardRuntime["onSave"];
  setData: Dispatch<SetStateAction<Record<string, ExploreCard | MarkdownCard>>>;
  setLayout: Dispatch<SetStateAction<DashboardV1State["layout"]>>;
  unlock(): void;
}) {
  const orgId = useApplicationContext().org_id!;
  const { token } = theme.useToken();
  const [describing, setDescribing] = useState(false);
  const [editingQuery, setEditingQuery] = useState(false);
  const [editingCard, setEditingCard] = useState(false);
  const [mapping, setMapping] = useState(false);
  const [visualizing, setVisualizing] = useState<
    | React.KeyboardEvent<HTMLElement>
    | React.MouseEvent<HTMLElement, MouseEvent>
    | undefined
  >(undefined);
  const [snippetting, setSnippetting] = useState<
    | React.KeyboardEvent<HTMLElement>
    | React.MouseEvent<HTMLElement, MouseEvent>
    | undefined
  >(undefined);
  const snippets = useSnippets({
    isForCard: false,
    onlyUserOwned: true,
  }).list;
  const variations = useSnippets({
    isForCard: true,
    isTagged: props.card.snippetTags,
  }).list;
  const [variation, setVariation] = useState("none");
  const hoverRef = useRef(null);
  const isHovering = useHover(hoverRef);
  const [modal, setModal] = useState<JSX.Element | null>(null);

  // secondary menus
  const popup = usePopupContext();
  const menuRefs = useRef({
    popup,
    context: props.context,
    stopVisualizing() {
      setVisualizing(undefined);
    },
    stopSnippetting() {
      setSnippetting(undefined);
    },
  });
  menuRefs.current = {
    popup,
    context: props.context,
    stopVisualizing() {
      setVisualizing(undefined);
    },
    stopSnippetting() {
      setSnippetting(undefined);
    },
  };

  // open the snippetting menu
  useEffect(() => {
    if (snippetting) {
      menuRefs.current.popup.setPopup(
        snippetting,
        <SnippetMenu
          snippets={snippets}
          onChange={(snippet) => {
            const ctx = menuRefs.current.context.params;
            const state: Partial<SearchState> = {
              duration: ctx.state.duration,
              startDate: ctx.state.startDate,
              endDate: ctx.state.endDate,
              select: snippet?.select || ctx.state.select,
              search: snippet?.search || ctx.state.search,
              aggregations: snippet?.aggregations || ctx.state.aggregations,
              groupBys: snippet?.groupBys || ctx.state.groupBys,
              filters: snippet?.filters || [],
              compareWith: snippet?.compareWith || ctx.state.compareWith,
            };
            const fKeys = new Set(state.filters!.map((f) => asKey(f)));
            ctx.state.filters?.forEach((filter) => {
              const fKey = asKey(filter);
              if (!fKeys.has(fKey)) {
                state.filters?.push(filter);
              }
            });
            if (state.filters?.length === 0) delete state.filters;
            ctx.dispatch({
              type: SearchActions.loadState,
              value: serializeParams(state),
            });
            menuRefs.current.stopSnippetting();
          }}
        />
      );
    }
  }, [snippetting, snippets]);

  const [initialViewValue, setInitialViewValue] = useState(
    props.context.view.value
  );
  useEffect(() => {
    if (!initialViewValue && props.context.view.value)
      setInitialViewValue(props.context.view.value);
  }, [initialViewValue, props.context.view.value]);

  // open the visualizing menu
  useEffect(() => {
    if (visualizing) {
      menuRefs.current.popup.setPopup(
        visualizing,
        <VisualizationMenu
          state={menuRefs.current.context.params.state}
          onChange={(value) => menuRefs.current.context.view.set(value)}
          value={menuRefs.current.context.view.value}
          default={initialViewValue}
          onClose={() => menuRefs.current.stopVisualizing()}
          isGrid={true}
        />
      );
    }
  }, [visualizing, initialViewValue]);

  // Close the secondary menus
  useEffect(() => {
    if (!snippetting && !visualizing) menuRefs.current.popup.close();
  }, [visualizing, snippetting]);

  const { filters } = useEntitySelectors({ state: props.context.params.state });
  const items = filters.map((filter) => ({
    key: asKey(filter),
    label: asKey(filter),
    value: asKey(filter),
  }));
  items.unshift({
    key: "default",
    label: "Default Mapping",
    value: "default",
  });

  const app = useApplicationContext();

  return (
    <>
      {modal}
      {editingCard &&
        createPortal(
          <QuestionEditor
            card={props.card}
            updateCard={async (cardData) => {
              return new Promise<Record<string, ExploreCard | MarkdownCard>>(
                (y) => {
                  props.setData((state) => {
                    const questionCard = state[props.cardId];
                    if (questionCard.type !== DeepLinkType.EXPLORE)
                      throw new Error("Expected question card");
                    state[props.cardId] = cardData;
                    y(state);
                    return state;
                  });
                }
              ).then((data) => {
                props.saveDashboard({ data });
              });
            }}
            onClose={() => setEditingCard(false)}
            okText="Save"
          />,
          document.body
        )}
      {editingQuery && (
        <ModalQueryEditor
          initial={props.context.params.state}
          onOk={(state) => {
            props.context.params.dispatch({
              type: SearchActions.loadState,
              value: serializeParams(state),
            });
          }}
          onClose={() => setEditingQuery(false)}
        />
      )}
      <div ref={hoverRef}>
        <div
          style={{
            position: "absolute",
            backgroundColor: isHovering
              ? `${token.colorBgBase}ee`
              : `${token.colorBgLayout}ee`,
            backdropFilter: "blur(1px)",
            top: TITLEBAR_HEIGHT,
            left: 2,
            borderRadius: 14,
            width: "calc(100% - 4px)",
            height: "calc(100% - 34px)",
            overflow: "auto",
            zIndex: 10_000, // come on joey, what are you doing.
            display: mapping ? "flex" : "none",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 16,
          }}
        >
          <div>
            <span style={{ fontSize: 13 }} className={mono.className}>
              Activity.Actor {"->"}
            </span>
            <Select
              showSearch
              style={{ width: 200, display: "block" }}
              options={items}
              filterOption={true}
              value={
                props.card.mappings?.find(
                  (m) =>
                    m.type === "condition" &&
                    m.fromFieldKey ===
                      asKey({ target: "Activity", attribute: "Actor" })
                )?.toFieldKey || "default"
              }
              onChange={(mapKey) => {
                const cardData = cloneData(props.card);
                cardData.mappings =
                  !mapKey || mapKey === "default"
                    ? []
                    : [
                        {
                          type: "condition",
                          fromFieldKey: asKey({
                            target: "Activity",
                            attribute: "Actor",
                          }),
                          toFieldKey: mapKey,
                        },
                      ];

                new Promise<Record<string, ExploreCard | MarkdownCard>>((y) => {
                  props.setData((state) => {
                    const questionCard = state[props.cardId];
                    if (questionCard.type !== DeepLinkType.EXPLORE)
                      throw new Error("Expected question card");
                    state[props.cardId] = cardData;
                    y(state);
                    return state;
                  });
                }).then((data) => {
                  props.saveDashboard({ data });
                  setMapping(false);
                });
              }}
            />
          </div>
          <Button shape="round" size="small" onClick={() => setMapping(false)}>
            cancel
          </Button>
        </div>
        <div
          style={{
            position: "absolute",
            backgroundColor: isHovering
              ? `${token.colorBgBase}ee`
              : `${token.colorBgContainer}ee`,
            backdropFilter: "blur(1px)",
            top: TITLEBAR_HEIGHT,
            left: 2,
            borderRadius: 14,
            width: "calc(100% - 4px)",
            height: "calc(100% - 34px)",
            overflow: "hidden",
            zIndex: 10_000, // come on joey, what are you doing.
            fontSize: 13,
            fontWeight: 200,
            display: describing ? "block" : "none",
            cursor: "pointer",
          }}
          onClick={() => setDescribing(false)}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              overflow: "auto",
              padding: 12,
            }}
          >
            <TextualQuery
              state={props.context.params.state}
              Val={({ children }) => (
                <span
                  className={mono.className}
                  style={{
                    fontWeight: 600,
                  }}
                >
                  {children}
                </span>
              )}
              Sep={() => <div style={{ height: 6 }} />}
            />
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingQuery(true);
              }}
            >
              edit
            </Button>
          </div>
        </div>
        <Flex
          centered
          gap={4}
          style={{ height: TITLEBAR_HEIGHT, marginRight: 4 }}
        >
          {props.card.centerTitle && (
            <div style={{ width: 34, visibility: "hidden" }} />
          )}
          <div
            style={{
              gap: 3,
              flex: 1,
              minWidth: 0,
              maxWidth: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: props.card.centerTitle ? "center" : undefined,
              height: "28px",
            }}
          >
            <div
              style={{
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textTransform: "lowercase",
                fontVariant: "all-small-caps",
                whiteSpace: "nowrap",
                fontWeight: 600,
                fontSize: 14,
                fontStyle: props.changed ? "italic" : "normal",
              }}
            >
              {props.card.showTitle &&
                ((props.card.snippetTags?.length || 0) > 0 &&
                variations.length > 0 ? (
                  <Select
                    className="pendo-dashboard-question-snippets"
                    bordered={false}
                    options={[
                      {
                        value: "none",
                        label: (
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: "lowercase",
                              fontVariant: "all-small-caps",
                            }}
                          >
                            {props.card.title}
                          </span>
                        ),
                      },
                      ...variations.map((link) => ({
                        value: link.id,
                        label: (
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 14,
                              textTransform: "lowercase",
                              fontVariant: "all-small-caps",
                            }}
                          >
                            {link.name}
                          </span>
                        ),
                      })),
                    ]}
                    popupMatchSelectWidth={false}
                    value={variation}
                    onChange={(id) => {
                      setVariation(id);
                      const snippet = variations.find((link) => link.id === id);
                      const params = (
                        snippet?.json as SnippetV1State | undefined
                      )?.params;
                      if (params) props.setBaseParams(params);
                      else props.resetBaseParams();
                    }}
                    style={{ marginLeft: 4 }}
                  />
                ) : (
                  <span style={{ paddingLeft: 10, display: "inline-block" }}>
                    {props.card.title}
                  </span>
                ))}
            </div>
            {props.changed && (
              <span style={{}}>
                <AsteriskIcon size={8} style={{ transform: "rotate(45deg)" }} />
              </span>
            )}
          </div>
          <div>
            {app.isSuperAdmin && (
              <>
                <DebugButton
                  context={props.context}
                  title={`Debug: ${props.context.doc.title || "Query"}`}
                />
                <Tooltip title="Result Insights">
                  <Button
                    size="small"
                    shape="circle"
                    type="text"
                    style={{ paddingBottom: -2 }}
                    onClick={() => {
                      setModal(
                        <ChatAssistant
                          title={props.context.doc.title}
                          initialPrompt={`Analyze the data for ${props.context.doc.title}`}
                          dataSources={[
                            {
                              query: props.context.params.state,
                              format: "csv",
                            },
                          ]}
                          timeframe={{
                            duration:
                              props.context.params.state.duration || "P7D",
                          }}
                          onClose={() => setModal(null)}
                        />
                      );
                    }}
                  >
                    <SparkleIcon size={12} />
                  </Button>
                </Tooltip>
              </>
            )}
            <Dropdown
              className="pendo-dashboard-question-menu"
              trigger={["click"]}
              menu={{
                items: [
                  {
                    key: "snippets",
                    disabled: snippets.length === 0,
                    label: "Apply snippet",
                    icon: <FilterOutlined />,
                    onClick(info) {
                      setSnippetting(info.domEvent);
                    },
                  },
                  {
                    key: "view",
                    label: "Show another view",
                    icon: <BarChartOutlined />,
                    onClick(info) {
                      setVisualizing(info.domEvent);
                    },
                  },
                  {
                    key: "reset",
                    label: "Reset to card defaults",
                    disabled: !props.changed,
                    icon: <UndoOutlined />,
                    onClick() {
                      props.onReset();
                    },
                  },
                  {
                    key: "map",
                    label: "Map dashboard filters",
                    disabled: false,
                    icon: <ApiOutlined />,
                    onClick() {
                      setMapping(true);
                    },
                  },
                  { type: "divider" },
                  {
                    key: "edit",
                    label: "Edit card details",
                    icon: <EditOutlined />,
                    onClick() {
                      setEditingCard(true);
                    },
                  },
                  {
                    key: "rearrange",
                    label: "Move or resize",
                    icon: <FullscreenOutlined />,
                    onClick() {
                      props.unlock();
                    },
                  },
                  {
                    key: "duplicate",
                    label: "Duplicate card below",
                    icon: <CopyOutlined />,
                    onClick() {
                      const ogId = props.cardId;
                      const dupeId = uuidv4();
                      Promise.all([
                        new Promise<Record<string, ExploreCard | MarkdownCard>>(
                          (resolve) => {
                            props.setData((state) => {
                              const newState = {
                                ...state,
                                [dupeId]: {
                                  ...props.card,
                                  title: `${props.card.title} - (COPY)`,
                                },
                              };
                              resolve(newState);
                              return newState;
                            });
                          }
                        ),
                        new Promise<Layout[]>((resolve) => {
                          props.setLayout((layouts) => {
                            resolve(layouts);
                            const ogLayout = layouts.find(
                              (layout) => layout.i === ogId
                            )!;
                            const newLayouts = [
                              ...layouts.map((layout) => ({
                                ...layout,
                                y:
                                  layout.y +
                                  (layout.y > ogLayout.y ? ogLayout.h : 0),
                              })),
                              {
                                ...ogLayout,
                                y: ogLayout.y + ogLayout.h,
                                i: dupeId,
                              },
                            ];
                            resolve(newLayouts);
                            return newLayouts;
                          });
                        }),
                      ]).then(() => {
                        props.unlock();
                      });
                    },
                  },
                  { type: "divider" },
                  {
                    key: "explore",
                    label: (
                      <Link
                        href={{
                          pathname: "/[orgId]/explore",
                          query: {
                            orgId,
                            s: serializeParams(
                              ensureState(props.context.params.state)
                            ),
                            v: serializeContentView(props.context.view),
                            d: serializeDocument({
                              saved: false,
                              title: props.card.title,
                            }),
                            t: serializeTabs({}),
                          },
                        }}
                      >
                        Explore this query
                      </Link>
                    ),
                    icon: <LinkOutlined />,
                  },
                ],
              }}
            >
              <Button size="small" shape="circle" type="text">
                <EllipsisOutlined />
              </Button>
            </Dropdown>
          </div>
        </Flex>
      </div>
    </>
  );
}

function UnlockedQuestion(
  props: ComponentProps<typeof LockedQuestion> & {
    onRemoveCard(id: string): void;
    setEditor(editor: null | JSX.Element): void;
    setCardData(cardData: DashboardV1State["data"][string]): void;
  }
) {
  const card = props.json.data[props.cardId];
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");
  return (
    <div style={{ height: "100%", position: "relative" }}>
      <div
        style={{
          height: "100%",
          width: "100%",
          zIndex: 1,
          opacity: 0.666,
        }}
      >
        <LockedQuestion {...props} />
      </div>
      <div
        style={{
          zIndex: 2,
          height: "100%",
          width: "100%",
          padding: 4,
          position: "absolute",
          left: 0,
          top: 0,
          cursor: "move",
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          alignItems: "flex-start",
        }}
      >
        <Button
          onMouseDownCapture={() => {
            props.setEditor(
              <QuestionEditor
                card={card}
                updateCard={(cardData) => {
                  props.setCardData(cardData);
                }}
                onClose={() => {
                  props.setEditor(null);
                }}
              />
            );
          }}
          title="Edit Question"
          size="small"
          shape="circle"
          type="primary"
          style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        >
          <span style={{ fontWeight: 600 }}>
            <EditOutlined />
          </span>
        </Button>
        <Button
          onMouseDownCapture={() => {
            props.onRemoveCard(props.cardId);
          }}
          title="Remove this card"
          size="small"
          shape="circle"
          danger
          type="primary"
          style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        >
          <span style={{ fontWeight: 600 }}>&times;</span>
        </Button>
      </div>
    </div>
  );
}

export function useQuestionCard(props: {
  json: Pick<DashboardV1State, "data">;
  cardId: string;
  searchState: Partial<SearchState>;
}) {
  const orgId = useOrg().id!;
  const card = props.json.data[props.cardId];
  if (card.type !== DeepLinkType.EXPLORE)
    throw new Error("expected question card");

  const [unbasedParams, setUnbasedParams] = useState<Partial<SearchState>>(
    card.state.params
  );

  const searchState = ensureState(unbasedParams, orgId);
  const search = useSearch(searchState);
  const explore = useExplore(search, { view: card.state.viz });

  const initializer = useRef(explore.doc.setInitial);
  initializer.current = explore.doc.setInitial;
  const params = useRef(explore.params.state);
  params.current = explore.params.state;
  const dispatcher = useRef(explore.params.dispatch);
  dispatcher.current = explore.params.dispatch;
  const viewer = useRef(explore.view);
  viewer.current = explore.view;
  type Viz = typeof card.state.viz;

  // persisted upstream changes, base question or pivot variation
  const paramsCheckpoint = serializeParams(searchState);
  const vizCheckpoint = JSON.stringify(card.state.viz || {});
  useEffect(() => {
    dispatcher.current({
      type: SearchActions.loadState,
      value: paramsCheckpoint,
    });
    const viz = JSON.parse(vizCheckpoint) as Viz;
    viewer.current.set(viz.value);
    viewer.current.setConfig(viz.config);
    initializer.current(paramsCheckpoint);
  }, [paramsCheckpoint, vizCheckpoint]);

  // This is the thing that applies the global state to the current card state
  // This is for after-the-fact changes to the global state.
  const { duration, startDate, endDate } = props.searchState;
  const needsDateChange =
    duration !== explore.params.state.duration ||
    startDate !== explore.params.state.startDate ||
    endDate !== explore.params.state.endDate;

  // effect for syncing upstream state changes, in this case, the Dates
  useEffect(() => {
    if (needsDateChange) {
      if (duration) {
        dispatcher.current({
          type: SearchActions.setDuration,
          value: duration,
        });
      } else if (startDate && endDate) {
        dispatcher.current({
          type: SearchActions.setDates,
          value: [startDate, endDate],
        });
      }
    }
  }, [needsDateChange, duration, startDate, endDate, paramsCheckpoint]);

  const globalFilters = useMemo(() => {
    if (!props.searchState.filters) return [];
    if (!card.mappings || card.mappings.length === 0)
      return props.searchState.filters;
    return props.searchState.filters.map((filter) => {
      const unMappedKey = asKey(filter);
      const mapping = card.mappings?.find(
        (m) => m.fromFieldKey === unMappedKey
      );
      if (!mapping) return filter;
      return { ...filter, ...fromKey(mapping.toFieldKey) };
    });
  }, [props.searchState.filters, card.mappings]);

  const mergeFilters = useCallback(
    // TODO bug, it should detect removals from prop.searchState.filter
    (filters: Condition[]) => {
      const substitutions = new Map<string, Condition>();

      globalFilters.forEach((f) => {
        const fKey = asKey(f);
        substitutions.set(fKey, f);
      });

      const merged = filters
        .map((f) => {
          const fKey = asKey(f);
          const replacement = substitutions.get(fKey);
          if (replacement) substitutions.delete(fKey);
          return replacement || f;
        })
        .filter(Boolean);
      merged.push(...substitutions.values());
      return merged;
    },
    [globalFilters]
  );

  useEffect(() => {
    const value = mergeFilters(params.current.filters || []);
    dispatcher.current({ type: SearchActions.setFilters, value });
  }, [mergeFilters]);

  const setBaseParams = useCallback(
    (state: Partial<SearchState>) => {
      const filters = mergeFilters(state.filters || []);
      setUnbasedParams({ ...state, filters });
    },
    [mergeFilters]
  );
  const resetBaseParams = useCallback(
    () => setBaseParams(card.state.params),
    [card.state.params, setBaseParams]
  );

  const initial = paramsCheckpoint;
  const resetState = useMemo(() => {
    const { duration, startDate, endDate } = props.searchState;
    const stateWithGlobalDate = {
      ...unserializeParams(initial),
      duration,
      startDate,
      endDate,
    };
    const filters = mergeFilters(stateWithGlobalDate.filters || []);
    return serializeParams({ ...stateWithGlobalDate, filters });
  }, [initial, mergeFilters, props.searchState]);

  // You are wondering, what is the difference between
  // reset and resetBaseParams. Plain old reset is basically
  // reset TO base params, and resetBaseParams changes what it
  // is reset to. Includes the original visualization
  const [initialViewType] = useState(explore.view.value);
  const reset = useCallback(() => {
    dispatcher.current({ type: SearchActions.loadState, value: resetState });
    viewer.current.set(initialViewType);
  }, [resetState, initialViewType]);

  // check for live card changes
  const changed = useMemo(() => {
    // probably should check viz, too
    return resetState !== explore.params.serialized;
  }, [resetState, explore.params.serialized]);

  return {
    explore,
    changed: useDebounce(changed, 100), // avoid flicker while settling
    reset,
    baseParams: unbasedParams,
    resetBaseParams,
    setBaseParams,
  };
}
